import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import EditLocationAltTwoToneIcon from '@mui/icons-material/EditLocationAltTwoTone';
import { MapView } from '../../shared/map';
import MapViewDialog from '../../shared/map/map-view-dialog';
import { currentMapZoom, GoogleMapPlaceResult } from '../../shared/map/google-maps-helper';
import { Extents } from '../../types/geo';
import { useApp } from '../../hooks/use-app';
import Button from '../../components/button';

const StyledBox = styled(Box)({
  boxSizing: 'border-box',
  height: '270px',
  border: '0.5px solid #C7C7CC',
  borderRadius: '8px',
  marginBottom: '8px',
  zIndex: 100,
});
export interface Props {
  mapExtents: Extents,
  saveMapExtents: (extents: Extents) => void,
  onLocationChange: (lat: number, lng: number, address?: string) => void,
}

const LocationField: React.FC<Props> = ({
  mapExtents, saveMapExtents, onLocationChange,
}: Props) => {
  const app = useApp();

  const [label, setLabel] = useState<string>('');
  const [locationIsSet, setLocationIsSet] = useState(false);
  const [openMapDialog, setOpenMapDialog] = useState<boolean>(false);
  const [currentExtents, setCurrentExtents] = useState<Extents>(mapExtents);

  useEffect(() => {
    const { latitude, longitude } = currentExtents;
    if (latitude && longitude) {
      // @ts-ignore
      const latLngGeo = new google.maps.LatLng(latitude, longitude);
      // @ts-ignore
      new google.maps.Geocoder().geocode({ location: latLngGeo }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const [currentDir] = results;
          setLabel(currentDir?.formatted_address || '');
        }
      });
      setLocationIsSet(latitude !== 0 && longitude !== 0);
    }
  }, [mapExtents]);

  const handleOnLocationChange = (response: GoogleMapPlaceResult) => {
    const newExtents = {
      ...currentExtents,
      latitude: response.coordinates.lat,
      longitude: response.coordinates.lng,
      zoom: currentMapZoom({
        ...currentExtents,
        latitude: response.coordinates.lat,
        longitude: response.coordinates.lng,
      }),
    };
    setCurrentExtents(newExtents);
    onLocationChange(response.coordinates.lat || 0, response.coordinates.lng || 0, response.label || '');
  };

  const OpenMapDialogButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  }));

  const renderMapView = () => (
    <StyledBox>
      <MapView
        mapExtents={currentExtents}
        marker={{ lat: currentExtents.latitude, lng: currentExtents.longitude }}
      />
    </StyledBox>
  );

  const dialogExtents = locationIsSet ? mapExtents : app.preferences.issueEditorMapExtents;

  return (
    <Box mt={2}>
      {locationIsSet && (
        <Box mt={2} mb={2}>
          {renderMapView()}
          <Typography color="rgba(0, 0, 0, 0.6)">{`${currentExtents.latitude}, ${currentExtents.longitude}`}</Typography>
          <Typography>{label}</Typography>
        </Box>
      )}
      <OpenMapDialogButton
        size="small"
        variant="outlined"
        color="primary"
        sx={{ fontWeight: 'bold' }}
        startIcon={<EditLocationAltTwoToneIcon />}
        onClick={() => setOpenMapDialog(true)}
      >
        Edit Location
      </OpenMapDialogButton>
      {openMapDialog && (
        <MapViewDialog
          extents={dialogExtents}
          saveMapExtents={saveMapExtents}
          title="Edit location"
          label={label}
          open={openMapDialog}
          onCloseClick={() => setOpenMapDialog(false)}
          onNewLocationSet={handleOnLocationChange}
        />
      )}
    </Box>
  );
};

export default LocationField;
